// 치과 설정 — 데스크 접수 QR (/settings/walkin-qr) — 10-04: 제목·문구 정본(§3-2) 교체, [QR 만들기] 앞 처리위탁 확인(DeskClinicConsentCard) 게이트 (2026-10-01 대표 확정, 기획서 부록 4 보강)
// users/{clinicUid}.walkinSlug 없으면 [QR 만들기] → 접수 URL + QR + [포스터 인쇄](A4) + [QR 다시 만들기](이전 QR 무효 경고)
//  - 발급·재발급 = callable issueWalkinSlug(asia-northeast3, services/clinicWalkinApi.js) — 계약 §F P2-6(10-01 밤). 서버가 난수 생성·walkinSlugs 예약·users.walkinSlug 기록.
//    users.walkinSlug 클라이언트 updateDoc 은 rules 로 거부됨(종전 방식 폐기). 치과 **대표 본인만**(직원은 permission-denied → 읽기 전용 안내). 1시간 10회 제한.
//    표시는 응답 slug 를 먼저 쓰고, onSnapshot(users.walkinSlug)이 따라오면 그 값으로 통일. 포스터 문구 = 약관 v1.2 §5B-4.
//  - 플래그(config/features.clinicWalkin, isClinicWalkinOn — clinicWalkin 단독) OFF 면 안내만.
//  - 포스터 문구는 사실만 — 혜택·할인·무료 표현 금지(의료법).
import React, { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Printer, RefreshCw } from 'lucide-react';
import { db } from '../../firebase/config';
import PageHeader from '../common/PageHeader';
import { getFeatures, isClinicWalkinOn, isDeskReceptionOn } from '../../utils/features';
import { walkinUrl, walkinPosterLine, walkinPosterSmall, walkinSlugIssueErrorMessage } from '../../utils/clinicWalkin';
import { issueWalkinSlug, getWalkinSlug } from '../../services/clinicWalkinApi';
import DeskClinicConsentCard from '../clinic/DeskClinicConsentCard';

const PRINT_ID = 'clinic-walkin-qr-print';

function ClinicWalkinQr({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const isStaff = user?.userType === 'staff';
    const clinicUid = isStaff ? user?.companyId : user?.uid;   // 직원은 회사(치과 대표) 문서를 읽기만
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    const [flagOn, setFlagOn] = useState(null);   // null=로딩
    const [me, setMe] = useState(undefined);      // undefined=로딩, null=문서 없음
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);   // { type:'ok'|'err', text }
    const [issuedSlug, setIssuedSlug] = useState('');   // callable 응답 slug — 스냅샷이 같은 값으로 따라오면 비운다
    const issuedRef = useRef('');
    // 데스크 접수 처리위탁 확인(10-04) — 확인 전에는 [QR 만들기] 비활성. null=조회 중
    const [clinicConsentOk, setClinicConsentOk] = useState(null);
    // 🔒 QR 주소 조회 callable(2026-10-11 보안 P1-a) — 서버가 users.walkinSlug 를 비공개 문서로 옮긴 뒤의 정본. null=조회 중·미배포
    //   전환기 호환: callable 이 아직 없으면(미배포) 종전 users.walkinSlug 스냅샷 값을 그대로 쓴다 → 지금 동작 불변(회귀 0)
    const [serverSlug, setServerSlug] = useState(null);
    const [slugRev, setSlugRev] = useState(0);
    useEffect(() => {
        if (!clinicUid || !isClinic) return undefined;
        let alive = true;
        getWalkinSlug()
            .then(s => { if (alive) setServerSlug(s); })
            .catch(e => { console.error('[웹 접수 QR] QR 주소 조회 실패(종전 방식으로 표시):', e?.code || e?.details?.code || e?.message || e); if (alive) setServerSlug(null); });
        return () => { alive = false; };
    }, [clinicUid, isClinic, slugRev]);

    useEffect(() => {
        let alive = true;
        // 2026-10-03: 「접수」(deskReception) 데스크의 [데스크 QR 보기]가 이 화면을 재사용 — 같은 QR 주소라 둘 중 하나만 켜져도 연다
        getFeatures().then(f => { if (alive) setFlagOn(isClinicWalkinOn(f) || isDeskReceptionOn(f)); })
            .catch((e) => { console.error('[웹 접수 QR] 플래그 조회 실패:', e?.message || e); if (alive) setFlagOn(false); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!clinicUid || !isClinic) return;
        const unsub = onSnapshot(doc(db, 'users', clinicUid),
            (snap) => {
                const data = snap.exists() ? snap.data() : null;
                setMe(data);
                // 서버 기록이 응답 slug 와 같아지면 응답 보관값은 비운다(이후 다른 탭 재발급 등 변화는 스냅샷이 지배)
                if (issuedRef.current && String(data?.walkinSlug || '') === issuedRef.current) { issuedRef.current = ''; setIssuedSlug(''); }
            },
            (e) => { console.error('[웹 접수 QR] 설정 조회 실패:', e?.code || e?.message || e); setMe(null); });
        return () => unsub();
    }, [clinicUid, isClinic]);

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={QrCode} title="데스크 접수 QR" />
                <div style={S.emptyBox}>치과 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }
    if (flagOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={QrCode} title="데스크 접수 QR" />
                <div style={S.emptyBox}>접수 기능이 아직 열리지 않았습니다.</div>
            </div>
        );
    }

    const slug = String(issuedSlug || serverSlug || me?.walkinSlug || '').trim();
    const url = slug ? walkinUrl(slug) : '';
    // 치과명 우선순위 = 서버 resolveClinicWalkin(clinic-walkin.js — businessName > companyName > name)과 같게 — 포스터와 환자 화면 이름 통일 (10-04 검토 P2)
    const clinicName = me?.businessName || me?.companyName || me?.name || '';

    const issue = async (reissue) => {
        if (isStaff || busy || !clinicUid) return;
        if (!reissue && clinicConsentOk !== true) return;   // 처리위탁 확인 전 신규 발급 금지(재발급=이전 QR 무효화는 허용)
        if (reissue && !window.confirm('QR을 다시 만들면 지금까지 인쇄해 둔 QR은 더 이상 쓸 수 없습니다. 계속할까요?')) return;
        setBusy(true); setNotice(null);
        try {
            // 만들기·다시 만들기 둘 다 같은 callable — 서버가 이전 예약을 지우고 새 slug 를 users.walkinSlug 에 기록
            const r = await issueWalkinSlug();
            const got = String(r?.slug || '').trim();
            if (r?.ok !== true || !got) {
                console.error('[웹 접수 QR] 발급 응답 이상: ok=', r?.ok, 'slug 유무=', !!got);
                setNotice({ type: 'err', text: 'QR을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.' });
                return;
            }
            issuedRef.current = got; setIssuedSlug(got); setSlugRev(n => n + 1);
            setNotice({ type: 'ok', text: reissue ? '새 QR을 만들었습니다. 포스터를 다시 인쇄해 주세요.' : 'QR을 만들었습니다.' });
        } catch (e) {
            console.error('[웹 접수 QR] 발급 실패:', e?.code || e?.message || e, e?.details?.code || '');
            setNotice({ type: 'err', text: walkinSlugIssueErrorMessage(e) });
        } finally {
            setBusy(false);
        }
    };

    const print = () => {
        // 인쇄 영역만 보이게 하는 임시 스타일 (ClinicPatientQr.js 선례와 동일)
        const style = document.createElement('style');
        style.id = 'cwq-print-style';
        style.textContent = `@media print { body * { visibility: hidden !important; } #${PRINT_ID}, #${PRINT_ID} * { visibility: visible !important; } #${PRINT_ID} { position: fixed; left: 0; top: 0; width: 100%; } }`;
        document.head.appendChild(style);
        const cleanup = () => { try { document.head.removeChild(style); } catch (_) { /* 이미 제거됨 */ } window.removeEventListener('afterprint', cleanup); };
        window.addEventListener('afterprint', cleanup);
        setTimeout(() => window.print(), 60);
    };

    return (
        <div style={S.container}>
            <PageHeader icon={QrCode} title="데스크 접수 QR" subtitle="데스크에 붙이는 치과 고정 QR — 환자가 휴대폰으로 찍어 대기 순서를 받습니다. 처음 온 환자는 접수 카드 내용(생년월일·성별·휴대폰 번호·주소)도 적습니다" />

            {/* 처리위탁 확인(정본 §3-4) — [QR 만들기] 앞. 확인 완료면 아무것도 그리지 않는다(10-08 — 확인 완료 줄은 설정 › 환자 정보 처리위탁으로, 종전 showConfirmedLine) */}
            <DeskClinicConsentCard onState={(x) => setClinicConsentOk(x.loading ? null : x.confirmed)} />

            <div style={S.box}>
                {isStaff && <div style={S.readonly}>직원 계정은 확인만 할 수 있습니다. QR 만들기·다시 만들기는 치과 대표 계정에서 합니다.</div>}
                {flagOn === null || me === undefined ? (
                    <div style={S.help}>불러오는 중…</div>
                ) : !slug ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: 14, color: '#475569', marginBottom: 14 }}>아직 접수 QR이 없습니다.</div>
                        {!isStaff && <button type="button" disabled={busy || clinicConsentOk !== true} onClick={() => issue(false)} style={{ ...S.btnP, opacity: busy || clinicConsentOk !== true ? 0.5 : 1, cursor: clinicConsentOk !== true ? 'not-allowed' : 'pointer' }}><QrCode size={15} /> QR 만들기</button>}
                        {!isStaff && clinicConsentOk === false && <div style={S.help}>위 「데스크 접수 처리위탁 확인」을 먼저 해 주세요.</div>}
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ background: '#fff', padding: 8, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                <QRCodeSVG value={url} size={160} />
                            </div>
                            <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>접수 URL</div>
                                <div style={{ wordBreak: 'break-all', color: '#475569', fontSize: 12.5 }}>{url}</div>
                                <div style={{ marginTop: 8, fontSize: 12.5, color: '#64748b' }}>환자가 본인 휴대폰으로 찍습니다. 접수 내용은 「접수」 화면에 대기 순서대로 보이고, 접수 기록은 접수일부터 2년 보관한 뒤 자동 삭제됩니다. 진료기록부가 아니므로 진료 기록은 따로 작성해 주세요.</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                            <button type="button" onClick={print} style={S.btnP}><Printer size={15} /> 포스터 인쇄</button>
                            {!isStaff && <button type="button" disabled={busy} onClick={() => issue(true)} style={S.btnS}><RefreshCw size={15} /> QR 다시 만들기</button>}
                        </div>
                        {!isStaff && <div style={S.help}>QR을 다시 만들면 이전 QR은 바로 무효가 됩니다. 인쇄해 둔 포스터를 교체해 주세요.</div>}
                    </>
                )}
                {notice && <div style={notice.type === 'ok' ? S.ok : S.err}>{notice.text}</div>}
            </div>

            {/* 인쇄 전용 A4 포스터 — 화면에서는 숨김. 문구는 사실만(의료법) */}
            {slug && (
                <div id={PRINT_ID} style={{ display: 'none' }}>
                    <style>{`@media print { #${PRINT_ID} { display: block !important; } }`}</style>
                    <div style={{ width: '210mm', minHeight: '297mm', padding: '40mm 20mm', boxSizing: 'border-box', textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#0f172a' }}>
                        <div style={{ fontSize: 34, fontWeight: 800, marginBottom: 40 }}>{clinicName}</div>
                        <QRCodeSVG value={url} size={320} />
                        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 40, lineHeight: 1.6 }}>{walkinPosterLine(clinicName)}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 60 }}>{walkinPosterSmall(clinicName)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 720, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    btnP: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    btnS: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
};

export default ClinicWalkinQr;
